import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Search,
  Filter,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Users,
  Building2,
  User,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

import { ClientRow } from './ClientRow';
import { CLIENT_STATUSES, CLIENT_TYPES } from '../Utils/clientsSchema';

const ClientTable = ({
  clients = [],
  isLoading = false,
  error = null,
  onEdit,
  onDelete,
  onRefresh,
  // Pagination
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  startIndex,
  endIndex,
  onPageChange,
  onItemsPerPageChange,
  // Filters
  filters,
  onFiltersChange,
  onClearFilters
}) => {
  const [searchTerm, setSearchTerm] = useState(filters?.name || '');
  const [selectedStatus, setSelectedStatus] = useState(filters?.status || 'all');
  const [selectedType, setSelectedType] = useState(filters?.is_company || 'all');

  const handleSearch = (value) => {
    setSearchTerm(value);
    onFiltersChange?.({ ...filters, name: value });
  };

  const handleStatusFilter = (value) => {
    setSelectedStatus(value);
    onFiltersChange?.({ ...filters, status: value === 'all' ? '' : value });
  };

  const handleTypeFilter = (value) => {
    setSelectedType(value);
    onFiltersChange?.({ ...filters, is_company: value === 'all' ? '' : value });
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedStatus('all');
    setSelectedType('all');
    onClearFilters?.();
  };

  const hasActiveFilters = searchTerm || (selectedStatus !== 'all') || (selectedType !== 'all');

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
              Error al cargar clientes
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mb-4">
              {error}
            </p>
            {onRefresh && (
              <Button 
                onClick={onRefresh} 
                variant="outline" 
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reintentar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-md">
      <CardHeader className="bg-gradient-to-r from-orange-50/50 to-amber-50/30 dark:from-orange-900/20 dark:to-amber-900/20 border-b border-orange-100/50 dark:border-orange-800/30">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl text-slate-900 dark:text-slate-100">
                Clientes
              </CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-400">
                {isLoading ? 'Cargando...' : `${totalItems} clientes en total`}
              </CardDescription>
            </div>
          </div>
          
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar clientes..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 w-full sm:w-64 border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20"
              />
            </div>

            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-full sm:w-32 border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {CLIENT_STATUSES.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select value={selectedType} onValueChange={handleTypeFilter}>
              <SelectTrigger className="w-full sm:w-32 border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {CLIENT_TYPES.map((type) => (
                  <SelectItem key={type.value.toString()} value={type.value.toString()}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllFilters}
                className="text-slate-500 hover:text-orange-600 hover:bg-orange-50 border-slate-200/60 dark:border-slate-700/60"
              >
                Limpiar
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-4" />
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Cargando clientes...
              </p>
            </div>
          </div>
        ) : clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mb-4">
              {hasActiveFilters ? (
                <Search className="h-8 w-8 text-orange-500" />
              ) : (
                <Users className="h-8 w-8 text-orange-500" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {hasActiveFilters ? 'No se encontraron clientes' : 'No hay clientes'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {hasActiveFilters 
                ? 'Intenta ajustar tus filtros de búsqueda.'
                : 'Comienza agregando tu primer cliente.'
              }
            </p>
            {hasActiveFilters && (
              <Button 
                variant="outline" 
                onClick={clearAllFilters}
                className="border-orange-200 text-orange-600 hover:bg-orange-50"
              >
                Limpiar filtros
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50/50 dark:bg-slate-800/50">
                  <TableRow className="border-b border-slate-200/60 dark:border-slate-700/60">
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-orange-500" />
                        <span>Nombre</span>
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Email</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300">Teléfono</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300">País</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300 text-center">Tipo</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300 text-center">Estado</TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300 text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client, index) => (
                    <ClientRow
                      key={client.id || client._id || index}
                      client={client}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-200/60 dark:border-slate-700/60 bg-slate-50/30 dark:bg-slate-800/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Mostrando <span className="font-semibold text-orange-600">{startIndex}</span> a <span className="font-semibold text-orange-600">{endIndex}</span> de <span className="font-semibold text-orange-600">{totalItems}</span> clientes
                    </span>
                    
                    <Select 
                      value={itemsPerPage.toString()} 
                      onValueChange={(value) => onItemsPerPageChange(parseInt(value))}
                    >
                      <SelectTrigger className="w-24 h-8 border-slate-200/60 dark:border-slate-700/60">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0 border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0 border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <span className="px-3 py-1 text-sm font-medium text-slate-700 dark:text-slate-300">
                      {currentPage} de {totalPages}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0 border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0 border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientTable; 